import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight, MdOutlineArrowForward } from 'react-icons/md';
import loadingGif from '../assets/images/loading.gif';

const ITEMS_PER_PAGE = 8;

const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export default function DiscoverItems({ title, pagination, allData, nfts, isOwnProfile }) {
  const [marketNfts, setMarketNfts] = useState([]);
  const [filteredPropNfts, setFilteredPropNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Получаем NFT с сервера (уже обогащённые данными владельца и отфильтрованные по admin)
  useEffect(() => {
    const fetchMarketNFTs = async () => {
      try {
        const response = await axios.get('https://nifty-art.com/api/nfts/market');
        const nftsWithOwners = await Promise.all(
          response.data.map(async (nft) => {
            try {
              const ownerResponse = await axios.get(`https://nifty-art.com/api/users/change/${nft.owner}`);
              // Проверяем, является ли пользователь администратором
              if (ownerResponse.data.admin === true || ownerResponse.data.admin === 'true') {
                return {
                  ...nft,
                  ownerUsername: ownerResponse.data.username,
                  ownerAvatar: ownerResponse.data.avatar || '/assets/images/avatar/2.jpg'
                };
              }
              return null;
            } catch (error) {
              return null;
            }
          })
        );
        const filteredNFTs = nftsWithOwners.filter((nft) => nft !== null);
        const shuffledNFTs = shuffleArray(filteredNFTs);

        // Предзагрузка изображений
        const imageUrls = [];
        shuffledNFTs.forEach(nft => {
          const mainUrl = nft.image?.startsWith('/uploads/nfts/')
            ? `https://nifty-art.com${nft.image}`
            : nft.image || 'https://via.placeholder.com/150';
          imageUrls.push(mainUrl);
          if (nft.ownerAvatar) {
            imageUrls.push(nft.ownerAvatar);
          }
        });

        const preloadImage = (url) =>
          new Promise((resolve) => {
            const img = new Image();
            img.src = url;
            img.onload = resolve;
            img.onerror = resolve;
          });

        await Promise.all(imageUrls.map(preloadImage));

        setMarketNfts(shuffledNFTs);
      } catch (error) {
        console.error('Error fetching NFTs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketNFTs();
  }, []);

  // Обрабатываем переданные через пропс NFT: дополняем данными владельца и фильтруем по admin
  useEffect(() => {
    const processPropNfts = async () => {
      if (!nfts) return;
      try {
        const processed = await Promise.all(
          nfts.map(async (nft) => {
            try {
              const ownerResponse = await axios.get(`https://nifty-art.com/api/users/change/${nft.owner}`);
              if (ownerResponse.data.admin === true || ownerResponse.data.admin === 'true') {
                return {
                  ...nft,
                  ownerUsername: ownerResponse.data.username,
                  ownerAvatar: ownerResponse.data.avatar || '/assets/images/avatar/2.jpg'
                };
              }
              return null;
            } catch (error) {
              return null;
            }
          })
        );
        const filtered = processed.filter(nft => nft !== null);
        setFilteredPropNfts(shuffleArray(filtered));
      } catch (error) {
        console.error('Error processing prop NFTs:', error);
      }
    };

    processPropNfts();
  }, [nfts]);

  // Если переданы NFT через пропс, используем отфильтрованные данные; иначе – marketNfts
  const itemsToDisplay = useMemo(() => {
    if (nfts) {
      return filteredPropNfts;
    }
    return marketNfts;
  }, [nfts, marketNfts, filteredPropNfts]);

  // Мемоизируем срез для текущей страницы
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return itemsToDisplay.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currentPage, itemsToDisplay]);

  // Обработчики пагинации
  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
  }, []);

  const handleNextPage = useCallback(() => {
    if ((currentPage - 1) * ITEMS_PER_PAGE + currentItems.length < itemsToDisplay.length) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, currentItems.length, itemsToDisplay.length]);

  return (
    <div className="container">
      <div className="grid grid-cols-1 text-center">
        <h3 className="md:text-[30px] text-[26px] font-semibold">{title}</h3>
      </div>

      {loading ? (
        <div className="flex justify-center items-center mt-10">
          <img src={loadingGif} alt="Loading..." />
        </div>
      ) : currentItems.length > 0 ? (
        <div className="grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 mt-10 gap-[30px]">
          {currentItems.map((item, index) => {
            const mediaSrc = item.image?.startsWith('/uploads/nfts/')
              ? `https://nifty-art.com${item.image}`
              : item.image || item.display_image_url || 'https://via.placeholder.com/150';

            return (
              <div key={index} className="group relative overflow-hidden p-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-100 dark:border-gray-800 hover:shadow-md dark:shadow-md hover:dark:shadow-gray-700 transition-all duration-500 hover:-mt-2 h-fit">
                <div className="relative overflow-hidden">
                  <div className="relative overflow-hidden rounded-lg">
                    <img
                      src={mediaSrc}
                      loading="lazy"
                      className="rounded-lg shadow-md dark:shadow-gray-700 group-hover:scale-110 transition-all duration-500"
                      alt={item.name}
                    />
                  </div>
                  {!isOwnProfile && (
                    <div className="absolute -bottom-20 group-hover:bottom-1/2 group-hover:translate-y-1/2 start-0 end-0 mx-auto text-center transition-all duration-500">
                      <Link
                        to={`/item-detail/${item.ownerUsername}/${item.contract}/${item.identifier}`}
                        className="btn btn-sm rounded-full bg-violet-600 hover:bg-violet-700 border-violet-600 hover:border-violet-700 text-white"
                      >
                        <i className="mdi mdi-lightning-bolt"></i> Buy Now
                      </Link>
                    </div>
                  )}
                </div>
                <div className="mt-3">
                  <div className="flex items-center">
                    <div className="relative inline-block">
                      <img src={item.ownerAvatar} className="rounded-full size-8" alt={item.ownerUsername} />
                      <i className="mdi mdi-check-decagram text-emerald-600 text-lg absolute -top-2 -end-2"></i>
                    </div>
                    <Link to={`/creator-profile/${item.ownerUsername}`} className="ms-2 text-[15px] font-medium text-slate-400 hover:text-violet-600">
                      {item.ownerUsername}
                    </Link>
                  </div>
                  <div className="my-3">
                    <Link to={`/item-detail/${item.ownerUsername}/${item.contract}/${item.identifier}`} className="font-semibold hover:text-violet-600">
                      {item.name}
                    </Link>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-50 dark:bg-slate-800 rounded-lg shadow dark:shadow-gray-700">
                    <div>
                      <span className="text-[16px] font-medium text-slate-400 block">Price</span>
                      <span className="text-[16px] font-semibold block">
                        {item.price} €
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-slate-400 mt-10">No NFTs available</p>
      )}

      {pagination && (
        <div className="grid md:grid-cols-12 grid-cols-1 mt-8">
          <div className="md:col-span-12 text-center">
            <nav>
              <ul className="inline-flex items-center -space-x-px">
                <li>
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="size-10 inline-flex justify-center items-center mx-1 rounded-full text-slate-400 bg-white dark:bg-slate-900 hover:text-white shadow-sm dark:shadow-gray-700 hover:border-violet-600 dark:hover:border-violet-600 hover:bg-violet-600 dark:hover:bg-violet-600"
                  >
                    <MdKeyboardArrowLeft className="text-[20px]" />
                  </button>
                </li>
                <li>
                  <span className="size-10 inline-flex justify-center items-center mx-1 rounded-full text-slate-400 bg-white dark:bg-slate-900 shadow-sm dark:shadow-gray-700">
                    {currentPage}
                  </span>
                </li>
                <li>
                  <button
                    onClick={handleNextPage}
                    disabled={(currentPage - 1) * ITEMS_PER_PAGE + currentItems.length >= itemsToDisplay.length}
                    className="size-10 inline-flex justify-center items-center mx-1 rounded-full text-slate-400 bg-white dark:bg-slate-900 hover:text-white shadow-sm dark:shadow-gray-700 hover:border-violet-600 dark:hover:border-violet-600 hover:bg-violet-600 dark:hover:bg-violet-600"
                  >
                    <MdKeyboardArrowRight className="text-[20px]" />
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {title && (
        <div className="grid grid-cols-1 mt-6">
          <div className="text-center">
            <Link
              to="/explore-one"
              className="btn btn-link text-[16px] font-medium hover:text-violet-600 after:bg-violet-600 duration-500 ease-in-out inline-flex items-center"
            >
              Explore More <MdOutlineArrowForward className="ms-1 text-sm" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
